import { TitleOption, _TOKEN_ } from "const";
import { createSelector } from "@reduxjs/toolkit";
import { optionByLocale, convertArrayToObject } from "utils/common-util";

export const RouterSelector = {
  state: (state) => state.router,
  location: (state) => RouterSelector.state(state).location,
};

export const LoaderSelector = {
  state: (state) => state.loader,
  visible: (state) => LoaderSelector.state(state).visible,
};

export const AuthSelector = {
  state: (state) => state.auth,
  loading: (state) => AuthSelector.state(state).loading,
  token: (state) => AuthSelector.state(state)[_TOKEN_],
  username: (state) => AuthSelector.state(state).username,
  program: (state) => AuthSelector.state(state).program,
  programState: (state) => AuthSelector.state(state).programState,
  forceHideAllMenu: (state) => AuthSelector.state(state).forceHideAllMenu,
  ssoError: (state) => AuthSelector.state(state).ssoError,
};

export const MasterSelector = {
  state: (state) => state.constant.master,
  locale: (state) => MasterSelector.state(state).locale.locale,
  loading: (state) => AuthSelector.state(state).loading,
  isLocaleTH: (state) => MasterSelector.state(state).locale.locale === "th",
  constants: (state) => MasterSelector.state(state).constants,
  hideButtonRegisterPrivateWealth: (state) => MasterSelector.constants(state).hideButtonRegisterPrivateWealth,
  hideFundNavigation: (state) => MasterSelector.constants(state).hideFundNavigation,
  hideMenuFundTransaction: (state) => MasterSelector.constants(state).hideMenuFundTransaction,
  errorConst: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.state(state).constants.errorConst],
    (isLocaleTH, errorConst) => {
      const errorArray = Object.keys(errorConst).map((errorCode) => {
        const errorConfig = errorConst[errorCode];
        return {
          ...errorConfig,
          textLocale: isLocaleTH ? errorConfig.textTh : errorConfig.textEn,
        };
      });

      return convertArrayToObject(errorArray, "errorCode");
    },
  ),
  errorMessageByCode: createSelector(
    [(state) => MasterSelector.errorConst(state), (_, errorCode) => errorCode],
    (errorConst, errorCode) => errorConst[errorCode]?.textLocale,
  ),
  receiveMethodList: (state) => MasterSelector.state(state).receiveMethodList,
  receiveMethodOptions: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.receiveMethodList(state)],
    (isLocaleTH, receiveMethodList) => {
      return optionByLocale(
        receiveMethodList,
        isLocaleTH,
        "receiveMethodCode",
        "receiveMethodDescTh",
        "receiveMethodDescEn",
      );
    },
  ),
  brokerList: (state) => MasterSelector.state(state).brokerList,
  brokerOptions: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.brokerList(state)],
    (isLocaleTH, brokerList) => {
      return optionByLocale(
        brokerList,
        isLocaleTH,
        "securityCompanyCode",
        "securityCompanyNameTh",
        "securityCompanyNameEn",
        {
          securityCompanyShortName: (_, option) => option.securityCompanyShortName,
          isForBondConnect: (_, option) => option.bondConnect === "Y",
          isCompany: (_, option) => option.securityCompany === "Y",
          securityCompanyNameDisplay: (isThai, option) => {
            const securityCompanyName = isThai ? option.securityCompanyNameTh : option.securityCompanyNameEn;
            return `${option.securityCompanyCode.padStart(3, "0")} ${
              option.securityCompanyShortName
            } : ${securityCompanyName}`;
          },
          labelFilters: (isThai, option) => [
            isThai
              ? `${option.securityCompanyCode.padStart(3, "0")}${option.securityCompanyShortName}:${
                  option.securityCompanyNameTh
                }`.toLowerCase()
              : `${option.securityCompanyCode.padStart(3, "0")}${option.securityCompanyShortName}:${
                  option.securityCompanyNameEn
                }`.toLowerCase(),
          ],
        },
      );
    },
  ),
  incomeSourceList: (state) => MasterSelector.state(state).incomeSourceList,
  incomeSourceOptions: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.incomeSourceList(state)],
    (isLocaleTH, incomeSourceList) => {
      return optionByLocale(
        incomeSourceList,
        isLocaleTH,
        "incomeSourceCode",
        "incomeSourceDescTh",
        "incomeSourceDescEn",
      );
    },
  ),
  incomeSourceListForSubscription: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.incomeSourceList(state)],
    (isLocaleTH, incomeSourceList) => {
      const incomeList = incomeSourceList.filter((i) => (typeof i.subscription === "boolean" ? i.subscription : true));
      return optionByLocale(incomeList, isLocaleTH, "incomeSourceCode", "incomeSourceDescTh", "incomeSourceDescEn");
    },
  ),
  incomeSourceOptionsForSubscription: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.incomeSourceList(state)],
    (isLocaleTH, incomeSourceList) => {
      const incomeList = incomeSourceList.filter((i) => (typeof i.subscription === "boolean" ? i.subscription : true));
      return optionByLocale(incomeList, isLocaleTH, "incomeSourceCode", "incomeSourceDescTh", "incomeSourceDescEn");
    },
  ),
  listOfOccupation: (state) => MasterSelector.state(state).listOfOccupation,
  occupationOption: createSelector(
    (state) => MasterSelector.listOfOccupation(state),
    (occupations) =>
      occupations.map((occupation) => ({
        label: occupation.occupationDescTh,
        value: occupation.occupationCode,
      })),
  ),
  listOfReceiveBenefits: (state) => MasterSelector.state(state).listOfReceiveBenefits,
  receiveBenefitsOption: createSelector(
    (state) => MasterSelector.listOfReceiveBenefits(state),
    (receiveBenefits) =>
      receiveBenefits.map((benefit) => ({
        label: benefit.receiveBenefitsDescTh,
        value: benefit.receiveBenefitsCode,
      })),
  ),
  listOfBranch: (state) => MasterSelector.state(state).listOfBranch,
  branchOption: createSelector([(state) => MasterSelector.listOfBranch(state)], (branches) =>
    branches.map((branch) => ({
      label: branch.branchName,
      value: branch.branchCode,
    })),
  ),
  listOfIncomeSource: (state) => MasterSelector.state(state).listOfIncomeSource,
  incomeSourceOption: createSelector([(state) => MasterSelector.listOfIncomeSource(state)], (incomeSources) =>
    incomeSources.map((incomeSource) => ({
      label: incomeSource.incomeSourceDescTh,
      value: incomeSource.incomeSourceCode,
    })),
  ),
  listOfChannel: (state) => MasterSelector.state(state).listOfChannel,
  channelOption: createSelector([(state) => MasterSelector.listOfChannel(state)], (channels) =>
    channels.map((channel) => ({
      label: channel.channelNameTh,
      value: channel.channelCode,
    })),
  ),
  titleOthersLoading: (state) => MasterSelector.state(state).titleOthers.loading,
  titleOthers: (state) => MasterSelector.state(state).titleOthers.options,
  isTitleOthersLangTh: (state) => MasterSelector.state(state).titleOthers.thai,
  titleOthersOption: createSelector(
    [(state) => MasterSelector.isTitleOthersLangTh(state), (state) => MasterSelector.titleOthers(state)],
    (isLocaleTH, titleOthers) => {
      const titleAlreadyShowInDropDown = (isLocaleTH ? TitleOption.TH : TitleOption.EN).map((op) =>
        op.value.toLowerCase(),
      );
      const titleOthersWithoutDuplicated = titleOthers.filter(
        (option) => !titleAlreadyShowInDropDown.includes(option.titleDesc.toLowerCase()),
      );
      return optionByLocale(titleOthersWithoutDuplicated, isLocaleTH, "titleDesc", "titleDesc", "titleDesc");
    },
  ),

  countryList: (state) => MasterSelector.state(state).listOfCountry,
  countryOptions: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.countryList(state)],
    (isLocaleTH, countryList) => {
      return optionByLocale(countryList, isLocaleTH, "countryCode", "countryName", "countryName");
    },
  ),

  pwmOccupationList: (state) => MasterSelector.state(state).listOfPwmOccupation,
  pwmOccupationOptions: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.pwmOccupationList(state)],
    (isLocaleTH, pwmOccupationList) => {
      return optionByLocale(pwmOccupationList, isLocaleTH, "occupationCode", "occupationDescTh", "occupationDescEn");
    },
  ),

  pwmBusinessTypeList: (state) => MasterSelector.state(state).listOfPwmBusinessType,
  pwmBusinessTypeOptions: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.pwmBusinessTypeList(state)],
    (isLocaleTH, pwmBusinessTypeList) => {
      return optionByLocale(
        pwmBusinessTypeList,
        isLocaleTH,
        "businessTypeCode",
        "businessTypeDescTh",
        "businessTypeDescEn",
      );
    },
  ),

  pwmMonthlyIncomeList: (state) => MasterSelector.state(state).listOfPwmMonthlyIncome,
  pwmMonthlyIncomeOptions: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.pwmMonthlyIncomeList(state)],
    (isLocaleTH, pwmMonthlyIncomeList) => {
      return optionByLocale(
        pwmMonthlyIncomeList,
        isLocaleTH,
        "monthlyIncomeCode",
        "monthlyIncomeDesc",
        "monthlyIncomeDesc",
      );
    },
  ),
  listOfFundSubType: (state) => MasterSelector.state(state).listOfFundSubType,
  listOfFundIssuer: (state) => MasterSelector.state(state).listOfFundIssuer,
  listOfFundDividend: (state) => MasterSelector.state(state).listOfFundDividend,
  fundDividendOptions: createSelector(
    [(state) => MasterSelector.isLocaleTH(state), (state) => MasterSelector.listOfFundDividend(state)],
    (isLocaleTH, listOfFundDividend) => {
      return listOfFundDividend?.map((fundDividend) => ({
        label: isLocaleTH ? fundDividend.nameTH : fundDividend.name,
        value: fundDividend.code,
      }));
    },
  ),
};

export const DisclaimerSelector = {
  state: (state) => state.disclaimer,
  loading: (state) => DisclaimerSelector.state(state).loading,
  accepted: (state) => DisclaimerSelector.state(state).accepted,
  investorPropertiesValid: (state) => DisclaimerSelector.state(state).investorPropertiesValid,
  disclaimers: (state) => DisclaimerSelector.state(state).disclaimers,
  disclaimerMessage: (state, code) => DisclaimerSelector.disclaimers(state)?.[code],
};

export const ProductSelector = {
  state: (state) => state.product,
  locale: (state) => ProductSelector.state(state).locale,
  loading: (state) => ProductSelector.state(state).loading,
  currentSecurity: (state) => ProductSelector.state(state).currentSecurity,
  securities: (state) => ProductSelector.state(state).securities,
  productDetail: (state) => ProductSelector.state(state).productDetail,
  filterSecurity: (state) => ProductSelector.state(state).filterSecurity,
  securitiesWithFilter: createSelector(
    [(state) => ProductSelector.securities(state), (state) => ProductSelector.filterSecurity(state)],
    (securities, filterSecurity) => {
      if (!securities || !filterSecurity) {
        return securities;
      }

      return securities.filter((security) => {
        const upperFilter = filterSecurity.toUpperCase();
        return (
          security.securityDisplay?.toUpperCase().includes(upperFilter) ||
          security.issuerCode?.toUpperCase().includes(upperFilter) ||
          security.issuerNameEn?.toUpperCase().includes(upperFilter) ||
          security.issuerNameTh?.toUpperCase().includes(upperFilter)
        );
      });
    },
  ),
  fetchingProductComplex: (state) => ProductSelector.state(state).fetchingProductComplex,
  paymentList: (state) => ProductSelector.state(state).paymentList,
  bannerLoading: (state) => ProductSelector.state(state).bannerLoading,
  banners: (state) => ProductSelector.state(state).banners,
  error: (state) => ProductSelector.state(state).error,
};

export const SubscriptionSelector = {
  state: (state) => state.subscription,
  loading: (state) => SubscriptionSelector.state(state).loading,
  error: (state) => SubscriptionSelector.state(state).error,
  setting: (state) => SubscriptionSelector.state(state).setting,
  unitDisplay: (state) => SubscriptionSelector.setting(state).unitDisplay,
  subscriptionDetail: (state) => SubscriptionSelector.state(state).subscriptionDetail,
  useLombard: (state) => SubscriptionSelector.subscriptionDetail(state).useLombard,
  fetchConfirmationInfoDone: (state) => SubscriptionSelector.state(state).fetchConfirmationInfoDone,
  sendFirstOTP: (state) => SubscriptionSelector.state(state).sendFirstOTP,
  refOtp: (state) => SubscriptionSelector.state(state).subscriptionDetail.refOtp,
  profileRegister: (state) => SubscriptionSelector.state(state).profileRegister,
  verifyReceiveAccount: (state) => SubscriptionSelector.state(state).verifyReceiveAccount,
  subscriptionCompleted: (state) => SubscriptionSelector.state(state).subscriptionCompleted,
  acceptAutoRedeemAccount: (state) => SubscriptionSelector.state(state).subscriptionDetail.acceptAutoRedeemAccount,
};

export const PWMSelector = {
  state: (state) => state.pwm,
  loading: (state) => PWMSelector.state(state).loading,
  profileRegister: (state) => PWMSelector.state(state).profileRegister,
  receiveMethodType: (state) => PWMSelector.state(state).receiveMethodType,
  receiveMethodSubType: (state) => PWMSelector.state(state).receiveMethodSubType,
  accountsType: (state) => PWMSelector.state(state).accountsType,
  profile: (state) => PWMSelector.state(state).userProfile,
  dividendAccountTypeOption: (state) => PWMSelector.state(state).dividendAccountTypeList,
  // taxDeductionTypeOption : (state) => PWMSelector.state(state).taxDeductionTypeList,
  maritalStatusOption: (state) => PWMSelector.state(state).maritalStatusList,
  investmentOption: (state) => PWMSelector.state(state).investmentList,
  accounts: (state) => PWMSelector.state(state).accountList,
  accountOptions: createSelector(
    [(state) => PWMSelector.accounts(state)],
    (accounts) =>
      accounts?.map((account) => ({
        label: `${account.accountNo} - ${account.branchName}`,
        value: account.accountId,
      })) ?? [],
  ),
  dataMemory: (state) => PWMSelector.state(state).dataMemory,
};
